import { useEffect, useState } from "react";
import { dashboardApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Globe } from "lucide-react";

const VAULT_LABEL = {
  TAX_CUSTODY: "Tax",
  SUPPLIER_OBLIGATION: "Supplier",
  CUSTOMER_TRUST: "Customer",
  INVENTORY_CUSTODY: "Inventory",
  OPERATING_CUSTODY: "Operating",
  FREE_EQUITY: "Free Equity",
  CREDIT_FACILITY_CUSTODY: "Credit",
  LOAN_OBLIGATION: "Loan",
  RESERVE_CAPITAL: "Reserve",
  STRATEGIC_CAPITAL: "Strategic",
};

const CURRENCY_FMT = {
  GBP: { symbol: "£", locale: "en-GB" },
  EUR: { symbol: "€", locale: "de-DE" },
  USD: { symbol: "$", locale: "en-US" },
  AED: { symbol: "د.إ", locale: "en-AE" },
  CAD: { symbol: "C$", locale: "en-CA" },
  AUD: { symbol: "A$", locale: "en-AU" },
};

function fmt(amount, currency) {
  const cfg = CURRENCY_FMT[currency] || { symbol: currency + " ", locale: "en-GB" };
  const v = Math.abs(Number(amount) || 0);
  const sign = (Number(amount) || 0) < 0 ? "-" : "+";
  return `${sign}${cfg.symbol}${v.toLocaleString(cfg.locale, { maximumFractionDigits: 0 })}`;
}

const VAULT_ORDER = [
  "FREE_EQUITY", "TAX_CUSTODY", "SUPPLIER_OBLIGATION",
  "CUSTOMER_TRUST", "INVENTORY_CUSTODY", "OPERATING_CUSTODY",
  "CREDIT_FACILITY_CUSTODY", "LOAN_OBLIGATION",
  "RESERVE_CAPITAL", "STRATEGIC_CAPITAL",
];

/**
 * Per-currency vault breakdown — shown only when at least two currencies
 * exist on the ledger. Wholesale / multi-currency businesses (Wise users
 * etc.) need to see EUR / USD / GBP balances side-by-side instead of
 * collapsed into a single reporting currency.
 */
export default function MultiCurrencyVaultStrip() {
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await dashboardApi.vaultTotalsByCurrency();
        if (!cancel) setTotals(res.data?.totals || {});
      } catch {
        if (!cancel) setTotals({});
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (!totals) return null;

  const allCurrencies = new Set();
  Object.values(totals).forEach((perCur) => {
    Object.keys(perCur).forEach((c) => allCurrencies.add(c));
  });
  const currencyList = Array.from(allCurrencies).sort();
  // Render only when two or more currencies exist — single-currency orgs
  // already have the standard vault cylinders.
  if (currencyList.length < 2) return null;

  const vaultsWithData = VAULT_ORDER.filter((v) => totals[v]);
  // Push any extra vaults not in VAULT_ORDER to the end.
  Object.keys(totals).forEach((v) => {
    if (!vaultsWithData.includes(v)) vaultsWithData.push(v);
  });

  return (
    <section data-testid="multi-currency-strip">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-barlow font-semibold text-xs text-slate-300 uppercase tracking-[0.2em] flex items-center gap-2"
            data-testid="multi-currency-title">
          <Globe className="w-3.5 h-3.5 text-cyan-400" />
          Per-Currency Vault Balances
        </h2>
        <span className="text-[10px] text-slate-500 font-mono">
          {currencyList.length} currencies · no FX conversion applied
        </span>
      </div>
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardContent className="p-4 md:p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-slate-500 uppercase tracking-[0.12em]">
                  <th className="text-left font-medium pb-2.5 pr-4">Vault</th>
                  {currencyList.map((c) => (
                    <th key={c} className="text-right font-medium pb-2.5 px-3 font-mono"
                        data-testid={`mc-col-${c}`}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vaultsWithData.map((vault) => {
                  const row = totals[vault] || {};
                  return (
                    <tr key={vault}
                        className="border-t border-slate-800/30"
                        data-testid={`mc-row-${vault}`}>
                      <td className="py-2.5 pr-4 text-slate-300">
                        {VAULT_LABEL[vault] || vault}
                      </td>
                      {currencyList.map((c) => {
                        const v = row[c];
                        if (v === undefined || v === 0) {
                          return (
                            <td key={c} className="py-2.5 px-3 text-right text-slate-700 font-mono">—</td>
                          );
                        }
                        return (
                          <td key={c}
                              className={`py-2.5 px-3 text-right font-mono ${v >= 0 ? "text-emerald-400" : "text-red-400"}`}
                              data-testid={`mc-cell-${vault}-${c}`}>
                            {fmt(v, c)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
            Each currency is reported in its native amount. We do not apply FX
            conversion — your real exchange rates are better than ours.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
